import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

/* V16 — "Médecine Augmentée"
   Based on V13 Cyber-Heritage structure but recolored with a medical/health palette.
   Deep clinical navy, healing emerald, medical sky blue, pharmacy amber.
   Cinzel (classical authority) + Rajdhani (clinical precision). */

const dark = '#0a1628';
const clean = '#e8f4f8';
const medGreen = '#10B981';
const medBlue = '#38BDF8';
const amber = '#F59E0B';
const dimText = '#6B8A9E';

const features = [
  { glyph: 'Ⅰ', title: 'Agenda clinique', desc: 'Planification intelligente des consultations, synchronisée en temps réel avec votre équipe.' },
  { glyph: 'Ⅱ', title: 'Dossiers patients', desc: 'Historiques médicaux complets, schémas dentaires et imagerie regroupés dans un espace sécurisé.' },
  { glyph: 'Ⅲ', title: 'Facturation santé', desc: 'Télétransmission Sésam-Vitale, devis conformes et suivi des règlements automatisé.' },
  { glyph: 'Ⅳ', title: 'Multi-cabinet', desc: 'Gestion centralisée de vos cliniques avec tableaux de bord unifiés par établissement.' },
];

export default function LandingV16() {
  const heritage = "'Cinzel', serif";
  const clinical = "'Rajdhani', sans-serif";

  return (
    <div className="doctif-page" style={{ fontFamily: clinical, background: dark, color: clean }}>
      <Helmet><title>doctif — Médecine Augmentée</title></Helmet>

      {/* Subtle medical texture overlay */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none', opacity: 0.025,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat', backgroundSize: '200px',
      }} />

      {/* Soft scanline for clinical feel */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(56,189,248,0.01) 2px, rgba(56,189,248,0.01) 4px)`,
      }} />

      {/* Medical green top line */}
      <div style={{
        height: 2,
        background: `linear-gradient(90deg, ${medGreen}, ${medBlue}, ${medGreen})`,
        boxShadow: `0 0 20px ${medGreen}44, 0 0 40px ${medBlue}22`,
      }} />

      {/* ── Header ── */}
      <header className="doctif-shell" style={{ padding: '28px 0 20px', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{
            fontFamily: heritage, fontSize: 30, fontWeight: 700, letterSpacing: '0.15em',
            textTransform: 'uppercase',
            background: `linear-gradient(135deg, ${clean}, ${medGreen})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            Doctif
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <nav className="doctif-chip-row">
              {Array.from({ length: 16 }, (_, i) => (
                <a key={i} className="doctif-chip" href={`/${i + 1}/`} style={{
                  borderRadius: 0, padding: '4px 10px', fontSize: 11, fontWeight: 600,
                  fontFamily: clinical,
                  background: i === 15 ? medGreen : 'transparent',
                  color: i === 15 ? dark : dimText,
                  border: `1px solid ${i === 15 ? medGreen : '#1e3a5f'}`,
                  boxShadow: i === 15 ? `0 0 12px ${medGreen}44` : 'none',
                }}>{String(i + 1).padStart(2, '0')}</a>
              ))}
            </nav>
            <a href="/accounts/login/" style={{
              fontFamily: heritage, fontWeight: 400, fontSize: 12, letterSpacing: '0.15em',
              textTransform: 'uppercase', color: medBlue, textDecoration: 'none',
              padding: '7px 18px', border: `1px solid ${medBlue}44`,
              boxShadow: `0 0 10px ${medBlue}22`, transition: 'all 0.3s',
            }}>Accès</a>
          </div>
        </div>
        {/* Decorative border — medical stripe */}
        <div style={{
          height: 1, marginTop: 16,
          background: `linear-gradient(90deg, transparent, ${medGreen}44, ${medBlue}44, ${medGreen}44, transparent)`,
        }} />
      </header>

      {/* ── Hero ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 60, paddingBottom: 80 }}>
        <p className="doctif-kicker doctif-fade-up" style={{
          color: medBlue, marginBottom: 24, letterSpacing: '0.35em',
          textShadow: `0 0 20px ${medBlue}33`,
        }}>
          Clinique ✦ Numérique
        </p>
        <h1 className="doctif-fade-up doctif-delay-1" style={{
          fontFamily: heritage, fontWeight: 700,
          fontSize: 'clamp(2.4rem, 6vw, 5.4rem)', lineHeight: 1,
          letterSpacing: '0.04em', textTransform: 'uppercase',
          marginBottom: 28,
        }}>
          <span style={{ color: clean }}>Médecine</span><br />
          <span style={{
            background: `linear-gradient(90deg, ${medGreen}, ${medBlue})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            textShadow: 'none',
          }}>Augmentée</span>
        </h1>
        <p className="doctif-fade-up doctif-delay-2" style={{
          fontSize: 16, lineHeight: 1.8, color: dimText, maxWidth: 480, marginBottom: 36,
          fontWeight: 500,
        }}>
          Là où le savoir-faire clinique rencontre la précision numérique. doctif fusionne
          l\'excellence médicale avec la puissance technologique.
        </p>
        <div className="doctif-fade-up doctif-delay-3" style={{ display: 'flex', gap: 14 }}>
          <a href="/accounts/login/" style={{
            display: 'inline-block', padding: '13px 32px',
            background: medGreen, color: dark, fontFamily: heritage,
            fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase',
            textDecoration: 'none', boxShadow: `0 0 24px ${medGreen}44`,
            transition: 'all 0.3s',
          }}>Commencer</a>
          <a href="#modules" style={{
            display: 'inline-block', padding: '13px 32px',
            border: `1px solid ${amber}44`, color: amber,
            fontFamily: heritage, fontWeight: 400, fontSize: 13, letterSpacing: '0.1em',
            textTransform: 'uppercase', textDecoration: 'none',
            transition: 'all 0.3s',
          }}>Explorer</a>
        </div>
      </section>

      {/* ── Stats — Medical-bordered bar ── */}
      <div style={{
        borderTop: `1px solid ${medBlue}22`, borderBottom: `1px solid ${medGreen}22`,
        position: 'relative', zIndex: 5, padding: '24px 0',
      }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', textAlign: 'center' }}>
          {[
            { v: '240+', l: 'Cliniques' },
            { v: '61K', l: 'Consultations / mois' },
            { v: '34%', l: 'Temps économisé' },
          ].map((s, i) => (
            <div key={i} style={{ borderRight: i < 2 ? `1px solid #162a45` : 'none' }}>
              <div style={{ fontFamily: heritage, fontSize: 28, color: clean, letterSpacing: '0.08em' }}>{s.v}</div>
              <div style={{ fontFamily: clinical, fontSize: 11, color: dimText, marginTop: 4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features — Medical cards with health accents ── */}
      <section id="modules" className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 72, paddingBottom: 72 }}>
        <p className="doctif-kicker" style={{ color: amber, letterSpacing: '0.3em', marginBottom: 12 }}>Modules</p>
        <h2 style={{
          fontFamily: heritage, fontWeight: 700, fontSize: 'clamp(1.5rem, 2.8vw, 2.4rem)',
          letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 48,
        }}>
          Quatre Piliers
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
          {features.map((f, i) => (
            <div key={i} className="doctif-fade-up" style={{
              padding: '32px 28px', position: 'relative',
              background: '#0e1f38', border: '1px solid #1e3a5f',
              animationDelay: `${i * 120}ms`,
              overflow: 'hidden',
            }}>
              {/* Medical corner accents */}
              <div aria-hidden style={{
                position: 'absolute', top: 0, left: 0, width: 30, height: 30,
                borderTop: `2px solid ${i % 2 === 0 ? medGreen : medBlue}`,
                borderLeft: `2px solid ${i % 2 === 0 ? medGreen : medBlue}`,
              }} />
              <div aria-hidden style={{
                position: 'absolute', bottom: 0, right: 0, width: 30, height: 30,
                borderBottom: `2px solid ${i % 2 === 0 ? medBlue : medGreen}`,
                borderRight: `2px solid ${i % 2 === 0 ? medBlue : medGreen}`,
              }} />
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 14 }}>
                <span style={{ fontFamily: heritage, fontSize: 32, color: amber, opacity: 0.5, letterSpacing: '0.05em' }}>{f.glyph}</span>
                <h3 style={{ fontFamily: heritage, fontWeight: 700, fontSize: 18, letterSpacing: '0.06em', textTransform: 'uppercase', color: clean }}>{f.title}</h3>
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: dimText, fontWeight: 500 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Dashboard Preview — Clinical panel ── */}
      <section style={{ position: 'relative', zIndex: 5, paddingBottom: 72 }}>
        <div className="doctif-shell">
          <div style={{
            background: '#0c1a30', border: '1px solid #1e3a5f', padding: 28,
            position: 'relative', overflow: 'hidden',
          }}>
            {/* Medical pattern top border */}
            <div style={{
              height: 3,
              background: `repeating-linear-gradient(90deg, ${medGreen}66 0px, ${medGreen}66 20px, transparent 20px, transparent 30px, ${medBlue}66 30px, ${medBlue}66 50px, transparent 50px, transparent 60px)`,
              marginBottom: 24,
            }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{ fontFamily: heritage, fontSize: 16, letterSpacing: '0.1em', textTransform: 'uppercase', color: clean }}>Consultations</span>
              <span style={{ fontFamily: clinical, fontSize: 11, color: medGreen, letterSpacing: '0.08em' }}>
                <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: medGreen, marginRight: 6, boxShadow: `0 0 8px ${medGreen}` }} />
                EN DIRECT
              </span>
            </div>
            {/* Entries */}
            {[
              { t: '09:00', p: 'Dr. Martin — Contrôle annuel', s: 'CONFIRMÉ', c: medGreen },
              { t: '09:45', p: 'Dr. Leroy — Détartrage', s: 'EN ATTENTE', c: amber },
              { t: '10:30', p: 'Dr. Fabre — Pose implant', s: 'CONFIRMÉ', c: medGreen },
            ].map((e, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '60px 1fr auto', gap: 20,
                padding: '14px 0', borderTop: '1px solid #162a45',
                alignItems: 'center',
              }}>
                <span style={{ fontFamily: clinical, fontSize: 13, color: dimText, fontWeight: 600 }}>{e.t}</span>
                <span style={{ fontFamily: clinical, fontSize: 14, color: '#8ba8c4', fontWeight: 500 }}>{e.p}</span>
                <span style={{
                  fontFamily: heritage, fontSize: 10, letterSpacing: '0.12em',
                  color: e.c, border: `1px solid ${e.c}33`, padding: '3px 10px',
                  boxShadow: `0 0 8px ${e.c}22`,
                }}>{e.s}</span>
              </div>
            ))}
            {/* Bottom medical pattern */}
            <div style={{
              height: 3, marginTop: 20,
              background: `repeating-linear-gradient(90deg, ${amber}44 0px, ${amber}44 20px, transparent 20px, transparent 30px, ${medGreen}44 30px, ${medGreen}44 50px, transparent 50px, transparent 60px)`,
            }} />
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingBottom: 72 }}>
        <p className="doctif-kicker" style={{ color: amber, letterSpacing: '0.3em', marginBottom: 32 }}>Témoignages</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {[
            { q: 'Le logiciel a transformé la gestion de notre clinique. Nos patients sont mieux suivis.', a: 'Dr. Elise Moreau', r: 'Chirurgien-dentiste, Paris' },
            { q: 'Interface claire, données sécurisées. Exactement ce dont un cabinet médical a besoin.', a: 'Dr. Julien Fabre', r: 'Orthodontiste, Lyon' },
            { q: 'La télétransmission et la facturation n\'ont jamais été aussi simples.', a: 'Dr. Nadia Hamdi', r: 'Parodontologue, Marseille' },
          ].map((t, i) => (
            <div key={i} style={{
              padding: '24px 20px', background: '#0e1f38', border: '1px solid #1e3a5f',
              position: 'relative',
            }}>
              <div aria-hidden style={{
                position: 'absolute', top: 0, right: 0, width: 20, height: 20,
                borderTop: `2px solid ${medGreen}`, borderRight: `2px solid ${medGreen}`,
              }} />
              <p style={{ fontFamily: heritage, fontStyle: 'italic', fontSize: 15, lineHeight: 1.7, color: '#8ba8c4', marginBottom: 16 }}>
                « {t.q} »
              </p>
              <p style={{ fontFamily: heritage, fontSize: 13, color: clean, letterSpacing: '0.05em' }}>{t.a}</p>
              <p style={{ fontFamily: clinical, fontSize: 11, color: dimText }}>{t.r}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust — Medical certifications ── */}
      <div style={{ borderTop: '1px solid #162a45', borderBottom: '1px solid #162a45', position: 'relative', zIndex: 5 }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', padding: '20px 0', textAlign: 'center' }}>
          {[{ k: 'Chiffrement', v: 'AES-256' }, { k: 'Hébergement', v: 'HDS France' }, { k: 'Conformité', v: 'RGPD' }, { k: 'Support', v: '< 2h' }].map((t, i) => (
            <div key={i} style={{ borderRight: i < 3 ? '1px solid #162a45' : 'none' }}>
              <div style={{ fontFamily: clinical, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: dimText, marginBottom: 4 }}>{t.k}</div>
              <div style={{ fontFamily: heritage, fontSize: 16, color: clean, letterSpacing: '0.05em' }}>{t.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
        <h2 style={{
          fontFamily: heritage, fontWeight: 700,
          fontSize: 'clamp(1.8rem, 4vw, 3.2rem)',
          letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1.1, marginBottom: 28,
        }}>
          Prêt à moderniser<br />
          <span style={{
            background: `linear-gradient(90deg, ${medGreen}, ${medBlue})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>votre clinique ?</span>
        </h2>
        <a href="/accounts/login/" style={{
          display: 'inline-block', padding: '14px 42px',
          background: `linear-gradient(135deg, ${medGreen}, ${medBlue})`,
          color: dark, fontFamily: heritage, fontWeight: 700, fontSize: 14,
          letterSpacing: '0.1em', textTransform: 'uppercase', textDecoration: 'none',
          boxShadow: `0 0 30px ${medGreen}33, 0 0 60px ${medBlue}22`,
          transition: 'all 0.3s',
        }}>Essayer gratuitement</a>
      </section>

      {/* ── Footer ── */}
      <footer style={{ position: 'relative', zIndex: 5, padding: '16px 0', borderTop: '1px solid #162a45' }}>
        <div className="doctif-shell" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: heritage, fontSize: 12, color: dimText, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Doctif — Médecine Augmentée
          </span>
          <span style={{ fontFamily: clinical, fontSize: 11, color: '#2a4a6a' }}>v16.0 — {new Date().getFullYear()}</span>
        </div>
      </footer>
      <div style={{
        height: 2,
        background: `linear-gradient(90deg, ${medBlue}, ${medGreen}, ${medBlue})`,
        boxShadow: `0 0 15px ${medGreen}33`,
      }} />
    </div>
  );
}
